// #region Imports
import UploadIcon from "@mui/icons-material/Upload";
import Tooltip from "@mui/material/Tooltip";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import {
  createMRTColumnHelper,
  type MRT_Row,
  type MRT_TableInstance,
  type MRT_TableOptions,
} from "material-react-table";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import React, { type ReactNode, useCallback, useMemo } from "react";
import { defaultStyles, FileIcon } from "react-file-icon";
import {
  getApiErrorData,
  getApiErrorMessage,
  getApiErrorStatus,
  getErrorMessageFromUnknown,
} from "_shared/_errors/api-error-message";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { removedTableRowCellStyles } from "_shared/fee-schedules/categories/_views/fee-schedule-table-cell";
import { useResizableColumns } from "_shared/_views/tables/table-column-widths";
import Guid from "_shared/guids/guid";
import Document from "documents/document";
import DocumentProcessingState from "documents/classification/_views/document-processing-state";
import DocumentDownloadControl from "documents/downloads/_views/document-download-control";
import useDocumentDownloadVerdicts from "documents/downloads/use-document-download-verdicts";
import { createBulkDocumentRequests } from "documents/_api/commands/documents-form-api-request";
import { useCreateDocumentsMutation } from "documents/_api/document-api";
import { NO_UPLOAD_ACCESS_TOOLTIP } from "permissions/access-requests/requestable-access";
import useSessionUser from "permissions/session/use-session-user";
import { getDocumentUploadSnackbar, summarizeDocumentUploadResults } from "documents/_api/document-upload-results";
import { knownLegacyDocumentErrors } from "documents/_errors/document-api-errors";
import { DocumentFileType } from "documents/document-file-type";
import { PROPOSAL_TOPIC_ENTITY_CLASS } from "documents/categories/document-category";
import DocumentTopic from "documents/topics/document-topic";
import UnavailableDocument from "documents/unavailable-documents/unavailable-document";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import {
  fieldHasPendingChange,
  fieldOrCommentFieldHasPendingChange,
  pendingChangeListItemStyles,
} from "work/proposals/comments/_view/pending-change-field-highlight";
import { proposalCollectionItemRevertValue } from "work/proposals/negotiation/proposal-collection-item-revert";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import ProposalFieldTimelineDropdown, {
  type ProposalFieldTimelineRevertEntry,
} from "work/proposals/negotiation/proposal-field-timeline-dropdown";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import ProposalInfoTooltip from "work/proposals/_views/proposal-info-tooltip";
import ProposalDeleteActionButton from "work/proposals/_views/_shared/proposal-delete-action-button";
import FieldUndoButton from "work/proposals/_views/_buttons/field-undo-button";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import { rowActionsColumnWidth } from "work/work-agreements/contracts/_views/document-collection-columns";
import {
  ADDED_BY_COLUMN_HEADER,
  ADDED_BY_COLUMN_ID,
  ADDED_BY_COLUMN_WIDTH,
  ADDED_COLUMN_HEADER,
  ADDED_COLUMN_ID,
  ADDED_COLUMN_WIDTH,
  DOCUMENT_COLUMN_HEADER,
  DOCUMENT_COLUMN_ID,
  DOCUMENT_COLUMN_WIDTH,
  DocumentCollectionHeader,
  DocumentCollectionHeading,
  DocumentCollectionNoRows,
  DocumentCollectionRoot,
  DocumentCollectionToolbar,
  DocumentLockCellContent,
  DocumentRowActions,
  documentCollectionBodyCellStyles,
  documentCollectionHeadCellStyles,
  documentCollectionTableOptions,
  compareDocumentsAdded,
  documentCreatedSearchText,
  DocumentAddedStamp,
  documentTypeLabel,
  FeeScheduleTableCellText,
  LOCKED_COLUMN_ID,
  LOCKED_COLUMN_WIDTH,
  ROW_ACTIONS_COLUMN_ID,
  TYPE_COLUMN_HEADER,
  TYPE_COLUMN_ID,
  TYPE_COLUMN_WIDTH,
  UNKNOWN_VALUE,
  useDocumentCollectionColumnFit,
} from "work/work-agreements/contracts/_views/document-collection-table";
import WorkDocument from "work/work-agreements/contracts/work-document";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
// #endregion

// #region Collection identity
/**
 * What a DRAFT proposal's document collections call themselves, and say about themselves.
 *
 * Deliberately not the active page's words. A draft has one party looking at it and no counterparty to
 * name a second list after, so a draft's collections are named for the reader — "Your Conflicts Checks",
 * "Your Policies" — while the active page splits policies into "Client Policies" and "Vendor Policies"
 * and calls the shared list "Conflict Documents". `conflicts.spec.ts` and `policies.spec.ts` pin both
 * vocabularies as they are rather than reconciling them, and the table's accessible name is this title,
 * because that is how `proposalDocumentList` finds a collection.
 */
const DRAFT_DOCUMENT_COLLECTIONS: Readonly<
  Partial<Record<WorkDocumentType, { title: string; about: string; uploadLabel: string }>>
> = {
  [WorkDocumentType.Conflicts]: {
    title: "Your Conflicts Checks",
    about:
      "Conflicts document(s) establish potential conflicts checks required by participating clients/vendors",
    uploadLabel: "Upload Conflicts Check",
  },
  [WorkDocumentType.ClientPolicy]: {
    title: "Your Policies",
    about: "Policy document(s) establish the policies required by participating clients/vendors",
    uploadLabel: "Upload Policy Document",
  },
  [WorkDocumentType.VendorPolicy]: {
    title: "Your Policies",
    about: "Policy document(s) establish the policies required by participating clients/vendors",
    uploadLabel: "Upload Policy Document",
  },
};

/**
 * What a collection of a kind this view does not recognise calls itself.
 *
 * A table has to have a name, and a collection that cannot say which kind it is still holds documents.
 * It gets the policies' words and, below, the whole-conflicts field — the same fallback the row controls
 * already took rather than borrowing another document's history.
 */
const UNRECOGNIZED_DOCUMENT_COLLECTION = {
  title: "Your Policies",
  about: "Policy document(s) establish the policies required by participating clients/vendors",
  uploadLabel: "Upload Policy Document",
};

/**
 * The history the collection's own header offers, exactly as on the active page.
 *
 * Conflicts names what it covers (#7074). Its history used to sit beside the waiver switch, next to
 * the waiver's own history and with the same icon, so neither said which one it was.
 */
const DRAFT_COLLECTION_HISTORIES: Readonly<
  Partial<Record<WorkDocumentType, { field: ProposalField; toolTip?: string }>>
> = {
  [WorkDocumentType.Conflicts]: {
    field: ProposalField.Conflicts,
    toolTip: "View conflicts documents change history",
  },
  [WorkDocumentType.ClientPolicy]: { field: ProposalField.ClientPolicies },
  [WorkDocumentType.VendorPolicy]: { field: ProposalField.VendorPolicies },
};
// #endregion

// #region Styles
/**
 * What the tab hands its sizing to.
 *
 * The collection root declares `overflow: hidden` of its own, and the draft tabs style this component
 * with `maxHeight: 60vh; overflowY: auto` — put on the same element those two fight, and the documents
 * past the first 60vh become unreachable. So the tab's class lands here, outside the root, exactly as
 * the active page hands its own class to a container around `DocumentRedlineSelection`'s root. It is
 * also what keeps the upload input a FOLLOWING SIBLING of the collection.
 */
const DocumentsContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  width: "100%",
}));
const DocumentCell = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  minWidth: 0,
  width: "100%",
}));
const DocumentNameLine = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",
  paddingRight: theme.spacing(1),
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const FileTypeIcon = styled("span")(({ theme }) => ({
  display: "inline-flex",
  flexShrink: 0,
  marginRight: theme.spacing(1),
  minWidth: "1.75rem",
  width: "1.75rem",
}));
const RowDownloadControl = styled(DocumentDownloadControl)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
// #endregion

// #region Types/Props
type SelectedDocumentListProps = {
  className?: string;
  documents?: WorkDocument[];
  documentType: WorkDocumentType;
  proposalId?: Guid;
  downloadingFileId: Guid | null;
  negotiationId?: string;
  viewerScope?: string;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  conflictsCheckWaived?: boolean;
  pendingChangeFields?: ProposalField[];
  onDownload: (documentId: Guid) => void;
  onDocumentRemoved: (documentId: Guid) => void;
  onDocumentAdded: (document: Document) => void;
  onDocumentsAdded?: (documents: Document[]) => void;
  /**
   * Called with a document reverted to a prior value (parsed from that value's history entry). The
   * caller applies it the same way it applies any other document edit (replacing the entry by id in
   * the selected-documents array and re-saving via its own builder setter), so the revert flows
   * through the same `queueProposalUpdate` path and lands as a `CollectionItemUpdated`/
   * `CollectionItemAdded` op — never a raw `FieldSet`.
   */
  onDocumentReverted?: (document: WorkDocument) => void;
  /**
   * A saved draft's documents removed since its last save, and their Undo (#6995). Each stays drawn,
   * struck through, with Undo in place of Remove until the save. Absent everywhere else.
   */
  draftRemovals?: DocumentDraftRemovals;
};

export type DocumentDraftRemovals = {
  documents: readonly WorkDocument[];
  onDocumentUndone: (document: WorkDocument) => void;
};

/** No draft removals: one array, so the rows below are not rebuilt on every render. */
const NO_DRAFT_REMOVED_DOCUMENTS: readonly WorkDocument[] = [];
// #endregion

/**
 * A DRAFT proposal's document collection — Your Conflicts Checks, or Your Policies.
 *
 * Drawn as the same table the active proposal's collections are (#4955, "like the Fee Schedule Tabs on
 * draft and active proposals"): four columns, each carrying one thing the document HAS — what it is
 * called, what kind of file it is, who put it there, and when — plus the Fee Schedule's whole toolbar,
 * a `⋮` menu on every data column, and real client-side sorting. The shape comes from
 * `document-collection-table.tsx`, which the active page's `DocumentRedlineSelection` draws from too,
 * so the two cannot drift apart.
 *
 * What is NOT shared with that component is its cells, and deliberately. A draft has no negotiation:
 * nothing here has been added by a counterparty, struck out, accepted or rejected, so there is no diff
 * to strike through and no decision to surround. Manufacturing `FieldRedline`s for a draft so it could
 * reuse those cells would invent redline lineage that no event ever produced, which is exactly what the
 * event-negotiation design forbids. The rows here are plain `WorkDocument`s and their cells say so.
 */
export default function SelectedDocumentList(props: Readonly<SelectedDocumentListProps>) {
  const {
    className,
    documents,
    documentType,
    proposalId,
    downloadingFileId,
    negotiationId,
    viewerScope,
    disableEditing,
    fieldLockTooltip,
    conflictsCheckWaived,
    pendingChangeFields,
    onDownload,
    onDocumentRemoved,
    onDocumentAdded,
    onDocumentsAdded,
    onDocumentReverted,
    draftRemovals,
  } = props;
  const draftRemovedDocuments = draftRemovals?.documents ?? NO_DRAFT_REMOVED_DOCUMENTS;

  const theme = useTheme();
  // Handed the media query as a string rather than as a `(theme) => …` callback: the callback form reads
  // the theme out of `@mui/system`'s own context, which is null wherever this table is rendered without
  // a `ThemeProvider` above it, and the row density is not worth a crash.
  const isMediumDisplaySize = useMediaQuery(theme.breakpoints.down("md"));
  const [isUploading, setIsUploading] = React.useState<boolean>(false);
  const [query, setQuery] = React.useState("");
  // The columns resize, and the container the table scrolls in is also what a double-clicked column is
  // measured in. There is no toolbar arrangement to choose any more: the toolbar wraps.
  const {
    containerRef: tableContainerRef,
    headCellProps: widthHeadCellProps,
    bodyCellProps: widthBodyCellProps,
  } = useResizableColumns<WorkDocument>();
  const [createDocuments] = useCreateDocumentsMutation();
  const sessionUser = useSessionUser();
  const documentFileUploaderRef = React.useRef<HTMLInputElement | null>(null);

  const collection = DRAFT_DOCUMENT_COLLECTIONS[documentType] ?? UNRECOGNIZED_DOCUMENT_COLLECTION;
  const collectionHistory = DRAFT_COLLECTION_HISTORIES[documentType];
  const groupField = collectionGroupField(documentType);
  const groupHasPendingChange = fieldOrCommentFieldHasPendingChange(pendingChangeFields, groupField);
  const showLockColumn = Boolean(disableEditing && fieldLockTooltip);
  // A draft's row offers Download, the field's history, and Remove where the collection may still be
  // edited — so its pinned column asks for less than the active page's five, and less again on a
  // collection nobody may change. The columns beside it keep the difference.
  const [collectionRef, columnFit] = useDocumentCollectionColumnFit({
    showLockColumn,
    rowActionsWidth: rowActionsColumnWidth(disableEditing ? 1 : 2),
    persistColumnVisibilityAs: "document-collection",
  });

  /**
   * The documents this collection draws.
   *
   * An `UnavailableDocument` is the server's refusal of an upload, not a document — it has an issue
   * where a name, a type and a stamp would be — so it never becomes a row. A `WorkDocument` the store
   * has marked unavailable is a different thing: it IS the reader's document, and it says so in its
   * Added column.
   *
   * A waived conflicts check leaves the collection with no rows AND no empty state: the reader has said
   * the question does not apply, and "No Documents" would be an answer to a question nobody asked.
   *
   * The documents a saved draft removed since its last save follow, struck through (#6995). They are
   * not in `documents`, so nothing the collection counts or saves includes them.
   */
  const rows = useMemo(() => {
    if (conflictsCheckWaived) return [];
    const current = (documents ?? []).filter(
      (document): document is WorkDocument => !(document instanceof UnavailableDocument),
    );
    return disableEditing ? current : [...current, ...draftRemovedDocuments];
  }, [conflictsCheckWaived, disableEditing, documents, draftRemovedDocuments]);
  const draftRemovedDocumentIds = useMemo(
    () => new Set(draftRemovedDocuments.map((document) => document.id.value)),
    [draftRemovedDocuments],
  );
  // A row is the work's projection of a document, which carries no verdict of its own. The documents
  // API's copy says whether this reader's download would be refused, and why (#6992).
  const downloadVerdicts = useDocumentDownloadVerdicts(rows.map((document) => document.id));

  // #region Added by
  // Who put a document on the proposal is a user id on the document, and a raw GUID is never a thing to
  // show a reader — nor to let a search match on, which is why it reaches neither the cell nor the
  // column's accessor. It is resolved through the same marketplace lookup the Fee Schedule's Team Member
  // column and the active page's collections use, and a document whose uploader cannot be named falls
  // back to a label (#4830).
  const creatorUserIds = useMemo(
    () => new Set(rows.flatMap((document) => (document.creatorId ? [document.creatorId.value] : []))),
    [rows],
  );
  const creatorIndividualsQuery = useGetIndividualsByUserIdsQuery(
    creatorUserIds.size > 0 ? { userIds: [...creatorUserIds] } : skipToken,
  );
  const creatorNames = useMemo(
    () =>
      new Map(
        (Individual.fromJSONArray(creatorIndividualsQuery.data, { allowEmpty: true }) ?? []).flatMap(
          (individual) => {
            const fullName = individual.fullName?.trim();
            return fullName ? [[individual.userId.value, fullName] as const] : [];
          },
        ),
      ),
    [creatorIndividualsQuery.data],
  );
  const creatorNameOf = useCallback(
    (document: WorkDocument | null | undefined): string => {
      if (!document?.creatorId) return UNKNOWN_VALUE;
      return creatorNames.get(document.creatorId.value) ?? UNKNOWN_VALUE;
    },
    [creatorNames],
  );
  // #endregion

  // #region Columns
  const columnHelper = useMemo(() => createMRTColumnHelper<WorkDocument>(), []);
  const columns = useMemo(
    () => [
      ...(showLockColumn
        ? [
          columnHelper.display({
            header: "",
            id: LOCKED_COLUMN_ID,
            size: LOCKED_COLUMN_WIDTH,
            enableColumnActions: false,
            enableSorting: false,
            muiTableBodyCellProps: { align: "center", sx: { verticalAlign: "middle" } },
            Cell: () => (
              <DocumentLockCellContent>
                <ReviewerEditLockIcon title={fieldLockTooltip} />
              </DocumentLockCellContent>
            ),
          }),
        ]
        : []),
      columnHelper.accessor(documentNameValue, {
        header: DOCUMENT_COLUMN_HEADER,
        id: DOCUMENT_COLUMN_ID,
        size: DOCUMENT_COLUMN_WIDTH,
        enableGlobalFilter: true,
        filterFn: "includesString",
        // Each cell draws exactly its accessor's value, so it draws the one MRT marked the search term in,
        // and keeps the plain value as the clipped cell's tooltip (#6590).
        Cell: ({ row, renderedCellValue }: { row: MRT_Row<WorkDocument>; renderedCellValue: ReactNode }) => {
          const extension = enumKeyOf(DocumentFileType, row.original.fileType).toLocaleLowerCase();
          return (
            <DocumentCell>
              <FileTypeIcon>
                <FileIcon extension={extension} {...defaultStyles[extension as keyof typeof defaultStyles]} />
              </FileTypeIcon>
              {/* `data-document-name` is how a browser test reads a collection's document names off the
                  rows (`proposalDocumentNames`): a row also carries the file-type icon, whose SVG spells
                  the extension out as real text, the type, the uploader, the upload stamp and a column
                  of controls, so the row's own text is not the document's name. */}
              <DocumentNameLine data-document-name="">
                <FeeScheduleTableCellText title={documentNameValue(row.original)}>
                  {renderedCellValue}
                </FeeScheduleTableCellText>
              </DocumentNameLine>
              <DocumentProcessingState
                status={downloadVerdicts.get(row.original.id.value)?.classificationStatus}
                scanStatus={downloadVerdicts.get(row.original.id.value)?.scanStatus}
              />
            </DocumentCell>
          );
        },
      }),
      columnHelper.accessor(documentTypeLabel, {
        header: TYPE_COLUMN_HEADER,
        id: TYPE_COLUMN_ID,
        size: TYPE_COLUMN_WIDTH,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ row, renderedCellValue }: { row: MRT_Row<WorkDocument>; renderedCellValue: ReactNode }) => (
          <FeeScheduleTableCellText title={documentTypeLabel(row.original)}>{renderedCellValue}</FeeScheduleTableCellText>
        ),
      }),
      columnHelper.accessor((document: WorkDocument) => creatorNameOf(document), {
        header: ADDED_BY_COLUMN_HEADER,
        id: ADDED_BY_COLUMN_ID,
        size: ADDED_BY_COLUMN_WIDTH,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ row, renderedCellValue }: { row: MRT_Row<WorkDocument>; renderedCellValue: ReactNode }) => (
          <FeeScheduleTableCellText title={creatorNameOf(row.original)}>{renderedCellValue}</FeeScheduleTableCellText>
        ),
      }),
      columnHelper.accessor(documentAddedValue, {
        header: ADDED_COLUMN_HEADER,
        id: ADDED_COLUMN_ID,
        size: ADDED_COLUMN_WIDTH,
        enableGlobalFilter: true,
        filterFn: "includesString",
        // By when the document was added, not by its text (#6713).
        sortingFn: (left, right) => compareDocumentsAdded(left.original, right.original),
        // The stamp reads the page's minute clock and shows its whole value on hover (#6713); the
        // accessor's text is only what the search reads.
        Cell: ({ row, renderedCellValue, table }: { row: MRT_Row<WorkDocument>; renderedCellValue: ReactNode; table: MRT_TableInstance<WorkDocument> }) =>
          row.original.isAvailable === false
            ? <FeeScheduleTableCellText title={documentAddedValue(row.original)}>{renderedCellValue}</FeeScheduleTableCellText>
            : <DocumentAddedStamp document={row.original} table={table} />,
      }),
    ],
    [columnHelper, creatorNameOf, downloadVerdicts, fieldLockTooltip, showLockColumn],
  );
  // #endregion

  // #region Row behaviour
  const handleDocumentRevert = useCallback(
    (entry: ProposalFieldTimelineRevertEntry) => {
      // This dropdown instance is always in collection-item mode (a document), so `entry.operation` is
      // always CollectionItemAdded/Updated at runtime — narrowed here to satisfy
      // `proposalCollectionItemRevertValue`'s parameter type, which excludes `FieldSet`.
      if (entry.operation?.kind !== "CollectionItemAdded" && entry.operation?.kind !== "CollectionItemUpdated") return;
      const revertValue = proposalCollectionItemRevertValue(entry.operation);
      if (revertValue === undefined) return;
      const revertedDocument = WorkDocument.fromJSON(revertValue as never);
      if (!revertedDocument) return;
      onDocumentReverted?.(revertedDocument);
    },
    [onDocumentReverted],
  );

  const renderRowActions = useCallback(
    (actionProps: { row: MRT_Row<WorkDocument> }) => {
      const document = actionProps.row.original;
      const field = documentField(documentType, document.id);
      const isUnavailable = document.isAvailable === false;
      const isDraftRemoved = draftRemovedDocumentIds.has(document.id.value);

      return (
        <DocumentRowActions>
          <RowDownloadControl
            document={downloadVerdicts.get(document.id.value)}
            name={document.name.value}
            onDownload={() => onDownload(document.id)}
            downloading={downloadingFileId?.isEqualTo(document.id)}
            disabled={downloadingFileId !== null || isUnavailable}
            disabledReason={isUnavailable ? "Document unavailable" : undefined}
            size="medium"
          />
          {isDraftRemoved && draftRemovals && (
            <FieldUndoButton onUndo={() => draftRemovals.onDocumentUndone(document)} />
          )}
          {!disableEditing && !isDraftRemoved && (
            <Tooltip title="Remove Document">
              <span>
                <ProposalDeleteActionButton
                  aria-label="Remove Document"
                  disabled={downloadingFileId !== null}
                  onClick={() => onDocumentRemoved(document.id)}
                />
              </span>
            </Tooltip>
          )}
          {/* The dropdown REVERTS, which is a write; the plain button only reads. A collection a
              reviewer may not edit gets the read-only history instead, the way it loses Remove. */}
          {disableEditing || isDraftRemoved ? (
            <ProposalFieldHistoryButton
              field={field}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
            />
          ) : (
            <ProposalFieldTimelineDropdown
              field={field}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
              onRevert={handleDocumentRevert}
            />
          )}
        </DocumentRowActions>
      );
    },
    [
      disableEditing,
      documentType,
      downloadVerdicts,
      downloadingFileId,
      draftRemovals,
      draftRemovedDocumentIds,
      handleDocumentRevert,
      negotiationId,
      onDocumentRemoved,
      onDownload,
      viewerScope,
    ],
  );
  // #endregion

  // #region Table plumbing
  const getRowId = useCallback(
    (document: WorkDocument, index: number) => document.id?.value ?? `document-${index}`,
    [],
  );

  const muiTableBodyRowProps = useCallback(
    (rowProps: { row: MRT_Row<WorkDocument> }) => {
      const hasPendingChange = fieldHasPendingChange(
        pendingChangeFields,
        documentField(documentType, rowProps.row.original.id),
      );
      // A document the draft removed since its last save says so on its row, as a removed team member's
      // row does, so a browser test can tell it from a current one without reading its styles (#6995).
      const isDraftRemoved = draftRemovedDocumentIds.has(rowProps.row.original.id.value);
      return {
        hover: false,
        sx: hasPendingChange ? pendingChangeListItemStyles(theme) : undefined,
        ...(isDraftRemoved && { "data-draft-removed-document": rowProps.row.original.id.value }),
      };
    },
    [documentType, draftRemovedDocumentIds, pendingChangeFields, theme],
  );

  const muiTableBodyCellProps = useCallback(
    (cellProps: { cell: { column: { id: string } }; row: MRT_Row<WorkDocument> }) => ({
      ...widthBodyCellProps(cellProps.cell.column.id),
      sx: {
        ...documentCollectionBodyCellStyles(theme, cellProps.cell.column.id),
        ...(draftRemovedDocumentIds.has(cellProps.row.original.id.value)
          && cellProps.cell.column.id !== ROW_ACTIONS_COLUMN_ID
          && removedTableRowCellStyles(theme)),
      },
    }),
    [draftRemovedDocumentIds, theme, widthBodyCellProps],
  );

  const muiTableHeadCellProps = useCallback(
    (headProps: { column: { id: string }; table: MRT_TableInstance<WorkDocument> }) => ({
      ...widthHeadCellProps(headProps.column.id, headProps.table),
      sx: documentCollectionHeadCellStyles(theme, headProps.column.id),
    }),
    [theme, widthHeadCellProps],
  );

  const renderEmptyRowsFallback = useCallback(
    ({ table }: { table: MRT_TableInstance<WorkDocument> }) =>
      conflictsCheckWaived ? <></> : <DocumentCollectionNoRows table={table} filtered={Boolean(query)} />,
    [conflictsCheckWaived, query],
  );

  // A draft collection has ONE way of gaining a document — the upload — so this is a single button
  // rather than a split one. It sits to the right of the search, and wraps underneath where the panel
  // is too narrow to hold both (#4955). A reader without Read/Write documents is offered it greyed
  // out, saying why: `Document.CreateAsync` would refuse the file (#6595), as on the active page.
  const mayUpload = sessionUser?.canUploadDocuments() === true;
  const addActions = useMemo(
    () =>
      disableEditing
        ? []
        : [
          {
            label: collection.uploadLabel,
            icon: <UploadIcon />,
            loading: isUploading,
            disabled: !mayUpload,
            disabledReason: mayUpload ? undefined : NO_UPLOAD_ACCESS_TOOLTIP,
            onClick: () => documentFileUploaderRef.current?.click(),
          },
        ],
    [collection.uploadLabel, disableEditing, isUploading, mayUpload],
  );

  // The collection's history leads the table's view buttons, on their row and grid (#6035).
  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<WorkDocument> }) => (
      <DocumentCollectionToolbar
        table={table}
        addActions={addActions}
        collectionActions={
          collectionHistory && (
            <ProposalFieldHistoryButton
              field={collectionHistory.field}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
              toolTip={collectionHistory.toolTip}
            />
          )
        }
      />
    ),
    [addActions, collectionHistory, negotiationId, viewerScope],
  );

  // An empty fragment rather than `null`: MRT reads this slot's result with `??`, so a nullish return
  // falls straight back to its own default buttons and the collection would carry two sets.
  const renderToolbarInternalActions = useCallback(() => <></>, []);

  const tableOptions: MRT_TableOptions<WorkDocument> = useMemo(
    () => ({
      ...documentCollectionTableOptions<WorkDocument>({
        tableName: collection.title,
        query,
        onQueryChange: setQuery,
        compactDensity: isMediumDisplaySize,
        tableContainerRef,
        columnFit,
      }),
      columns,
      data: rows,
      getRowId,
      muiTableBodyRowProps,
      muiTableBodyCellProps,
      muiTableHeadCellProps,
      renderEmptyRowsFallback,
      renderRowActions,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
    }),
    [
      collection.title,
      columnFit,
      columns,
      getRowId,
      isMediumDisplaySize,
      muiTableBodyCellProps,
      muiTableBodyRowProps,
      muiTableHeadCellProps,
      query,
      renderEmptyRowsFallback,
      renderRowActions,
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      rows,
      tableContainerRef,
    ],
  );
  // #endregion

  async function handleDocumentFileUpload(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    if (!event.currentTarget.files) return;

    const files = Array.from(event.currentTarget.files);
    if (files.length === 0) return;

    try {
      setIsUploading(true);
      let context: string | undefined;
      context = documentType === WorkDocumentType.Conflicts ? "Conflicts" : "Policy";
      const topics = [
        DocumentTopic.create({
          context: context,
        }),
      ];
      if (proposalId && !proposalId.isEmpty) {
        topics.push(
          DocumentTopic.create({
            entityClass: PROPOSAL_TOPIC_ENTITY_CLASS,
            entityId: proposalId,
          }),
        );
      }
      const requests = createBulkDocumentRequests(files, { topics });
      const results = await createDocuments({ requests }).unwrap();
      const summary = summarizeDocumentUploadResults(results, requests);

      if (summary.documents.length > 0) {
        if (onDocumentsAdded) {
          onDocumentsAdded(summary.documents);
        } else {
          for (const document of summary.documents) {
            onDocumentAdded(WorkDocument.fromDocument(document, documentType));
          }
        }
      }

      const snackbar = getDocumentUploadSnackbar(summary);
      enqueueSnackbar(snackbar.message, { variant: snackbar.variant });
    } catch (error) {
      const status = getApiErrorStatus(error);
      if (status === HttpStatusCode.UnsupportedMediaType) {
        enqueueSnackbar(
          getApiErrorMessage(getApiErrorData(error), { knownLegacyText: knownLegacyDocumentErrors })
            ?? "Unsupported file type",
          { variant: "error" },
        );
      } else if (status === HttpStatusCode.UnprocessableEntity) {
        enqueueSnackbar("Unsupported file type", { variant: "error" });
      } else if (status !== undefined) {
        enqueueSnackbar("Unknown error occurred during upload", { variant: "error" });
      } else {
        enqueueSnackbar(
          getErrorMessageFromUnknown(error) ?? "Unknown error occurred during upload",
          { variant: "error" },
        );
      }
    } finally {
      event.target.value = "";
      setIsUploading(false);
    }
  }

  return (
    <DocumentsContainer className={className}>
      <DocumentCollectionRoot data-document-collection="" ref={collectionRef}>
        <DocumentCollectionHeader
          data-document-collection-header=""
          sx={groupHasPendingChange ? pendingChangeListItemStyles(theme) : undefined}
        >
          {/* The title stays a direct text child of the heading, with the info tooltip beside it rather
              than around it: a wrapper would take the words out of the heading's own text node. */}
          <DocumentCollectionHeading>
            {collection.title}
            <ProposalInfoTooltip title={collection.about} placement="bottom" edge="end" />
          </DocumentCollectionHeading>
        </DocumentCollectionHeader>
        <MRTNoMemo<WorkDocument> options={tableOptions} columnChoice={columnFit.columnVisibility} />

      </DocumentCollectionRoot>
      {/* A FOLLOWING SIBLING of the collection, not a descendant: that is where
          `proposalDocumentUploadInput` reaches for it, and where the active page's collections put
          theirs. */}
      <input type="file" hidden={true} multiple={true} ref={documentFileUploaderRef} onChange={handleDocumentFileUpload} />
    </DocumentsContainer>
  );
}

// #region Row values
// Module-level, so the compiler leaves them alone and so each column's accessor is one named thing
// rather than an inline lambda whose meaning has to be reconstructed from its body.

function documentNameValue(document: WorkDocument): string {
  return document.name?.value ?? "";
}

/**
 * When the document joined the proposal — or the fact that it can no longer be reached.
 *
 * "Unavailable" goes where the stamp would, which is where the draft's list has always put it: a
 * document the store cannot serve is still the reader's document, and the honest thing to say about it
 * is that it is gone rather than a date that implies it is there.
 */
function documentAddedValue(document: WorkDocument): string {
  if (document.isAvailable === false) return "Unavailable";
  return documentCreatedSearchText(document);
}

/** The whole collection's field — what its heading's pending-change highlight is keyed off. */
function collectionGroupField(documentType: WorkDocumentType): ProposalField {
  if (documentType === WorkDocumentType.ClientPolicy) return ProposalField.ClientPolicies;
  if (documentType === WorkDocumentType.VendorPolicy) return ProposalField.VendorPolicies;
  return ProposalField.Conflicts;
}

/**
 * One document's own field — its history, its revert path, and its pending-change highlight.
 *
 * A collection of a kind this view does not recognise falls back to the whole-conflicts field rather
 * than silently borrowing another document's history.
 */
function documentField(documentType: WorkDocumentType, documentId: Guid): ProposalField {
  if (documentType === WorkDocumentType.Conflicts) return ProposalField.ConflictsDocument(documentId);
  if (documentType === WorkDocumentType.ClientPolicy) return ProposalField.ClientPolicy(documentId);
  if (documentType === WorkDocumentType.VendorPolicy) return ProposalField.VendorPolicy(documentId);
  return ProposalField.Conflicts;
}
// #endregion
